import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  type PlayerProfile,
  type RegistrationOrder,
  type Tournament,
} from "../lib/api";
import { formatMoney } from "../lib/money";
import GuideHelpButton from "../components/GuideHelpButton";

type ChargeMode = "existing" | "new";

interface ChargeResult {
  order: RegistrationOrder;
  created: boolean;
  payment_url: string;
}

export default function AdminChargeCreatePage() {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { token } = useAuth();
  const registrationsPath = `/admin/tournaments/${tournamentId}/registrations`;

  const [mode, setMode] = useState<ChargeMode>("existing");
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [currency, setCurrency] = useState("MXN");

  const [players, setPlayers] = useState<PlayerProfile[]>([]);
  const [pendingSearch, setPendingSearch] = useState("");
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
  const [searching, setSearching] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [amount, setAmount] = useState("");
  const [settingsWarning, setSettingsWarning] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ChargeResult | null>(null);
  const [copied, setCopied] = useState(false);

  const selectedPlayer = useMemo(
    () => players.find((p) => p.id === selectedPlayerId) ?? null,
    [players, selectedPlayerId],
  );

  const loadTournament = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    try {
      const response = await api.tournaments.get(token, tournamentId);
      setTournament(response.data.tournament);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    }
  }, [token, tournamentId]);

  const loadSettings = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    try {
      const response = await api.registrations.get(token, tournamentId);
      setAmount(response.data.settings.entry_fee_amount ?? "");
      setCurrency(response.data.settings.currency ?? "MXN");
      setSettingsWarning(null);
    } catch {
      // Flujo de dinero: no precargar "0" silencioso — el campo queda vacío
      // (required obliga captura consciente) y avisamos junto al monto.
      setSettingsWarning(
        "No pudimos cargar la cuota configurada del torneo — captura el monto manualmente.",
      );
    }
  }, [token, tournamentId]);

  const loadPlayers = useCallback(
    async (term: string) => {
      if (!token) return;
      setSearching(true);
      try {
        const response = await api.players.search(token, {
          search: term || undefined,
          limit: 50,
        });
        setPlayers(response.data.players);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message || "No pudimos cargar los jugadores.");
        } else {
          setError("Error de red. Intenta otra vez.");
        }
      } finally {
        setSearching(false);
      }
    },
    [token],
  );

  useEffect(() => {
    void loadTournament();
    void loadSettings();
    void loadPlayers("");
  }, [loadTournament, loadSettings, loadPlayers]);

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void loadPlayers(pendingSearch.trim());
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || Number.isNaN(tournamentId)) return;
    setError(null);

    if (mode === "existing" && !selectedPlayerId) {
      setError("Selecciona un jugador de la lista.");
      return;
    }
    if (mode === "new" && !firstName.trim()) {
      setError("El nombre es obligatorio.");
      return;
    }
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setError("El monto debe ser mayor a cero.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.registrations.createCharge(token, tournamentId, {
        player_profile_id: mode === "existing" ? selectedPlayerId : null,
        new_player:
          mode === "new"
            ? {
                first_name: firstName.trim(),
                last_name: lastName.trim() || null,
                phone: phone.trim() || null,
                email: email.trim() || null,
              }
            : null,
        amount: amount.trim(),
      });
      setResult(response.data);
      setCopied(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo generar el cobro.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const fullPaymentUrl = result ? `${window.location.origin}${result.payment_url}` : "";

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(fullPaymentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: el <code> visible permite copiar manual
    }
  }

  const missingEmailWarning =
    mode === "new" && !email.trim()
      ? "Sin correo, el jugador no recibirá la Bienvenida con su acceso — solo el link de pago."
      : null;

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  return (
    <>
      <section className="card">
        <div className="card__header-row">
          <div>
            <h1 className="card__title">
              <span className="hint-label">
                Cobrar inscripción
                {tournament ? ` · ${tournament.name}` : ""}
                <GuideHelpButton />
              </span>
            </h1>
            <p className="card__subtitle">
              Cobra a un jugador sin darlo de alta todavía: queda Preinscrito
              hasta que pague y lo actives.
            </p>
          </div>
          <div className="form__actions">
            <Link className="btn btn--ghost" to={registrationsPath}>
              Volver a inscripciones
            </Link>
          </div>
        </div>

        <div className="form__actions" style={{ marginBottom: "1rem" }}>
          <button
            type="button"
            className={mode === "existing" ? "btn" : "btn btn--ghost"}
            onClick={() => setMode("existing")}
            disabled={submitting}
          >
            Jugador existente
          </button>
          <button
            type="button"
            className={mode === "new" ? "btn" : "btn btn--ghost"}
            onClick={() => setMode("new")}
            disabled={submitting}
          >
            Crear perfil nuevo
          </button>
        </div>

        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
      </section>

      {mode === "existing" && (
        <section className="card">
          <h2 className="card__title">Buscar jugador</h2>
          <form className="form" onSubmit={handleSearch}>
            <label>
              Término de búsqueda
              <input
                type="search"
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
                placeholder="Nombre, email o teléfono"
              />
            </label>
            <div className="form__actions">
              <button className="btn" type="submit" disabled={searching}>
                {searching ? "Buscando…" : "Buscar"}
              </button>
            </div>
          </form>

          {players.length === 0 ? (
            <p className="muted">
              {searching ? "Buscando…" : "No hay resultados."}
            </p>
          ) : (
            <ul className="list">
              {players.map((p) => {
                const isSelected = p.id === selectedPlayerId;
                return (
                  <li
                    key={p.id}
                    style={{
                      borderColor: isSelected ? "rgba(183, 255, 0, 0.5)" : undefined,
                      background: isSelected ? "rgba(183, 255, 0, 0.06)" : undefined,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedPlayerId(p.id)}
                      aria-pressed={isSelected}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: 0,
                        border: "none",
                        background: "transparent",
                        color: "inherit",
                        font: "inherit",
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                    >
                      <strong>{p.display_name}</strong>
                      {p.city && <span className="muted"> · {p.city}</span>}
                      {p.phone && <span className="muted"> · {p.phone}</span>}
                      {p.email && <span className="muted"> · {p.email}</span>}
                      {isSelected && <span className="muted"> · seleccionado</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {mode === "new" && (
        <section className="card">
          <h2 className="card__title">Datos del nuevo perfil</h2>
          <form className="form" onSubmit={(e) => e.preventDefault()} aria-label="new-player-fields">
            <label>
              Nombre
              <input
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Apellido (opcional)
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Teléfono (opcional)
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Email (recomendado)
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
              />
            </label>
            {missingEmailWarning && <p className="muted">{missingEmailWarning}</p>}
          </form>
        </section>
      )}

      <section className="card">
        <h2 className="card__title">Monto a cobrar</h2>
        <form className="form" onSubmit={handleSubmit}>
          <label>
            Monto ({currency})
            <input
              type="number"
              step="0.01"
              min="0.01"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={submitting}
            />
          </label>
          {settingsWarning && (
            <p role="alert" className="form-error">
              {settingsWarning}
            </p>
          )}
          {mode === "existing" && selectedPlayer && (
            <p className="muted">
              Vas a cobrar a <strong>{selectedPlayer.display_name}</strong>.
            </p>
          )}
          <div className="form__actions">
            <button className="btn" type="submit" disabled={submitting}>
              {submitting ? "Generando…" : "Generar cobro"}
            </button>
          </div>
        </form>
      </section>

      {result && (
        <section className="card">
          <h2 className="card__title">
            {result.created ? "Cobro generado" : "Este jugador ya tenía una orden"}
          </h2>
          <p>
            Comparte este link para que el jugador pague su inscripción (
            {formatMoney(result.order.gross_amount, result.order.currency)}):
          </p>
          <code style={{ wordBreak: "break-all" }}>{fullPaymentUrl}</code>
          <div className="form__actions" style={{ marginTop: "0.75rem" }}>
            <button className="btn" type="button" onClick={copyLink}>
              {copied ? "¡Copiado!" : "Copiar link"}
            </button>
          </div>
          <p className="card__subtitle">
            El jugador queda como Preinscrito: no aparece en grupos ni rol de
            juego hasta que pague y lo actives.
          </p>
        </section>
      )}
    </>
  );
}
